import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminOrganizationService } from '../../../services/admin/admin-organization.service';
import { AdminSchoolService } from '../../../services/admin/admin-school.service';
import { OrganizationModel } from '../../../models/school/organization.model';
import { SchoolModel, SchoolFilter } from '../../../models/school/school.model';
import { ModalService } from '../../../shared/modals/modal/modal.service';
import { CountryModel } from '../../../models/localization-and-pricing/country.model';

@Component({
  selector: 'app-admin-organization-detail',
  templateUrl: './admin-organization-detail.component.html',
  styleUrls: ['./admin-organization-detail.component.css']
})
export class AdminOrganizationDetailComponent implements OnInit {

  organization: OrganizationModel = null;
  associatedSchools: SchoolModel[] = [];
  schoolSearchQuery = '';
  isLoading = false;
  isLoadingSchools = false;

  isEditing = false;
  isRemoving = false;
  selectedForRemoval = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private organizationSv: AdminOrganizationService,
    private schoolSv: AdminSchoolService,
    private modalSv: ModalService
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.params['organizationId'];
    this.loadOrganization(id);
  }

  loadOrganization(id: string) {
    this.isLoading = true;
    this.organizationSv.getOrganization(id).subscribe(organization => {
      this.organization = organization;
      this.isLoading = false;
      this.loadAssociatedSchools();
    }, () => {
      this.isLoading = false;
      this.modalSv.showAlertModal('Error', 'Could not load the organization. Please go back and try again.');
    });
  }

  loadAssociatedSchools() {
    if (!this.organization.schoolIds || this.organization.schoolIds.length === 0) {
      this.associatedSchools = [];
      return;
    }
    this.isLoadingSchools = true;
    const filter = new SchoolFilter();
    filter.schoolIds = this.organization.schoolIds.join(',');
    this.schoolSv.fetchSchools(filter).subscribe(schools => {
      this.associatedSchools = schools;
      this.isLoadingSchools = false;
    }, () => {
      this.isLoadingSchools = false;
      this.modalSv.showAlertModal('Error', 'Could not load associated schools. Please refresh the page.');
    });
  }

  get filteredSchools(): SchoolModel[] {
    if (!this.schoolSearchQuery) {
      return this.associatedSchools;
    }
    const q = this.schoolSearchQuery.toLowerCase();
    return this.associatedSchools.filter(s => s.name.toLowerCase().includes(q));
  }

  countryTitle(code: string): string {
    return CountryModel.title(code) || code;
  }

  countryIconUrl(code: string): string {
    return CountryModel.iconURL(code);
  }

  goBack() {
    this.router.navigate(['admin', 'organizations']);
  }

  openAssociate() {
    this.router.navigate(['admin', 'organizations', this.organization._id, 'associate']);
  }

  toggleEditing() {
    this.isEditing = !this.isEditing;
    this.selectedForRemoval.clear();
  }

  isSelectedForRemoval(schoolId: string): boolean {
    return this.selectedForRemoval.has(schoolId);
  }

  toggleSelectedForRemoval(schoolId: string) {
    if (this.selectedForRemoval.has(schoolId)) {
      this.selectedForRemoval.delete(schoolId);
    } else {
      this.selectedForRemoval.add(schoolId);
    }
  }

  get allSelectedForRemoval(): boolean {
    return this.filteredSchools.length > 0 && this.filteredSchools.every(s => this.selectedForRemoval.has(s._id));
  }

  get someSelectedForRemoval(): boolean {
    return this.filteredSchools.some(s => this.selectedForRemoval.has(s._id));
  }

  toggleSelectAllForRemoval(checked: boolean) {
    if (checked) {
      this.filteredSchools.forEach(s => this.selectedForRemoval.add(s._id));
    } else {
      this.filteredSchools.forEach(s => this.selectedForRemoval.delete(s._id));
    }
  }

  confirmRemoveSelected() {
    const count = this.selectedForRemoval.size;
    if (count === 0) {
      return;
    }
    const message = `This will remove ${count} school${count !== 1 ? 's' : ''} from this organization. Continue?`;
    this.modalSv.showChoiceModal('Remove schools', message).subscribe(confirmed => {
      if (confirmed) {
        this.removeSelected();
      }
    });
  }

  removeSelected() {
    const schoolIds = Array.from(this.selectedForRemoval);
    this.isRemoving = true;
    this.organizationSv.bulkRemoveSchoolsFromOrganization(schoolIds, this.organization._id).subscribe(result => {
      this.isRemoving = false;
      this.selectedForRemoval.clear();
      this.isEditing = false;
      this.loadOrganization(this.organization._id);
      if (result.failed > 0) {
        this.modalSv.showAlertModal('Some removals failed', `${result.removed} school${result.removed !== 1 ? 's' : ''} removed, ${result.failed} failed. Please try again.`);
      }
    }, () => {
      this.isRemoving = false;
      this.modalSv.showAlertModal('Error', 'Could not remove the selected schools. Please try again.');
    });
  }

  schoolInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name.substring(0, 2).toUpperCase();
  }
}
